/**
 * Adapter hooks that bridge the Messenger-specific API hooks to the
 * generic InboxHooks interface expected by shared-inbox components.
 */
import type { InboxHooks, GenericConversation, GenericMessage, GenericAgentConfig } from '@/components/shared-inbox/types';
import type { ConversationStatus } from '@/types/outreach';
import {
    useMessengerConversations,
    useMessengerConversationDetail,
    useMessengerConversationMessages,
    useMessengerSendAdminMessage,
    useMessengerUpdateConversationStatus,
    useMessengerMarkConversationRead,
    useMessengerAgentConfigs,
    useMessengerSaveAgentConfig,
    useMessengerDeleteAgentConfig,
} from './useMessengerInbox';

// ── Conversation list ────────────────────────────────────────────────────────

function useConversations(search?: string, status?: ConversationStatus | '') {
    const result = useMessengerConversations(search, status);
    return {
        ...result,
        data: result.data
            ? {
                  ...result.data,
                  pages: result.data.pages.map((page) => ({
                      count: page.count,
                      results: page.results.map(
                          (c): GenericConversation => ({
                              id: c.id,
                              primaryId: c.psid,
                              secondaryId: null,
                              customerName: c.customerName,
                              profilePicUrl: c.profilePicUrl,
                              status: c.status,
                              unreadCount: c.unreadCount,
                              updatedAt: c.updatedAt,
                              place: c.place,
                              lastMessage: c.lastMessage,
                              isWindowOpen: c.isWindowOpen,
                              windowExpiresAt: c.windowExpiresAt,
                          }),
                      ),
                  })),
              }
            : undefined,
    };
}

// ── Conversation detail ──────────────────────────────────────────────────────

function useConversationDetail(id: string | null) {
    const result = useMessengerConversationDetail(id);
    return {
        data: result.data
            ? ({
                  id: result.data.id,
                  primaryId: result.data.psid,
                  secondaryId: null,
                  customerName: result.data.customerName,
                  profilePicUrl: result.data.profilePicUrl,
                  status: result.data.status,
                  unreadCount: result.data.unreadCount,
                  updatedAt: result.data.updatedAt,
                  place: result.data.place,
                  lastMessage: result.data.lastMessage,
                  isWindowOpen: result.data.isWindowOpen,
                  windowExpiresAt: result.data.windowExpiresAt,
              } satisfies GenericConversation)
            : undefined,
    };
}

// ── Messages ─────────────────────────────────────────────────────────────────

function useConversationMessages(id: string | null) {
    const result = useMessengerConversationMessages(id);
    return {
        isLoading: result.isLoading,
        data: result.data
            ? {
                  count: result.data.count,
                  results: result.data.results.map(
                      (m): GenericMessage => ({
                          id: m.id,
                          conversationId: m.conversationId,
                          direction: m.direction,
                          messageType: m.messageType,
                          senderType: m.senderType,
                          content: m.content,
                          mediaUrl: m.mediaUrl,
                          timestamp: m.timestamp,
                          postbackPayload: m.postbackPayload,
                      }),
                  ),
              }
            : undefined,
    };
}

// ── Mutations ────────────────────────────────────────────────────────────────

const useSendAdminMessage = useMessengerSendAdminMessage;
const useUpdateConversationStatus = useMessengerUpdateConversationStatus;
const useMarkConversationRead = useMessengerMarkConversationRead;

// ── Agent configs ────────────────────────────────────────────────────────────

function useAgentConfigs() {
    const result = useMessengerAgentConfigs();
    return {
        data: result.data as GenericAgentConfig[] | undefined,
    };
}

function useSaveAgentConfig() {
    const mutation = useMessengerSaveAgentConfig();
    return {
        isPending: mutation.isPending,
        mutate: (
            config: GenericAgentConfig & { name: string; systemPrompt: string },
            options?: { onSuccess?: (saved: GenericAgentConfig) => void },
        ) => {
            mutation.mutate(config as Parameters<typeof mutation.mutate>[0], {
                onSuccess: (saved) => options?.onSuccess?.(saved as GenericAgentConfig),
            });
        },
    };
}

function useDeleteAgentConfig() {
    const mutation = useMessengerDeleteAgentConfig();
    return {
        isPending: mutation.isPending,
        mutate: (id: string, options?: { onSuccess?: () => void }) => {
            mutation.mutate(id, { onSuccess: options?.onSuccess });
        },
    };
}

// ── Export as InboxHooks ─────────────────────────────────────────────────────

export const messengerInboxHooks: InboxHooks = {
    useConversations,
    useConversationDetail,
    useConversationMessages,
    useSendAdminMessage,
    useUpdateConversationStatus,
    useMarkConversationRead,
    useAgentConfigs,
    useSaveAgentConfig,
    useDeleteAgentConfig,
};
